import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatIDR, errMsg } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { TrendingUp, FileText, Download, PieChart as PieIcon } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from "recharts";
import { useAuth } from "@/contexts/AuthContext";

export default function Investor() {
  const { user } = useAuth();
  const [amount, setAmount] = useState(user?.invested_amount || 25000000);
  const [years, setYears] = useState(5);
  const [sim, setSim] = useState(null);
  const [investorName, setInvestorName] = useState(user?.name || "");
  const [busy, setBusy] = useState(false);
  const agreementRef = useRef(null);

  const runSim = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/investor/simulate", { amount: Number(amount), years: Number(years) });
      setSim(data);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  useEffect(() => { runSim(); /* eslint-disable-next-line */ }, []);

  const downloadPDF = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const el = agreementRef.current;
    // Use html2canvas via jspdf.html
    await doc.html(el, {
      callback: (d) => d.save(`Perjanjian-Bagi-Hasil-${investorName || "Investor"}.pdf`),
      x: 10, y: 10, width: 190, windowWidth: 900, autoPaging: 'text',
    });
  };

  const chartData = sim?.projections?.map((p) => ({
    Tahun: `Thn ${p.year}`,
    "Saldo Akhir": p.balance,
    "Keuntungan Kumulatif": p.cumulative_profit,
  })) || [];

  return (
    <>
      <PageHeader title="Portal Investor" subtitle="Simulasi investasi 14% per tahun + Surat Perjanjian Bagi Hasil" testId="page-investor"/>

      <Tabs defaultValue="sim">
        <TabsList className="mb-4">
          <TabsTrigger value="sim" data-testid="tab-sim"><PieIcon className="w-4 h-4 mr-2"/>Simulasi</TabsTrigger>
          <TabsTrigger value="agreement" data-testid="tab-agreement"><FileText className="w-4 h-4 mr-2"/>Surat Perjanjian</TabsTrigger>
        </TabsList>

        <TabsContent value="sim">
          <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Nominal Investasi (Rp)</Label>
                <Input type="number" value={amount} step="1000000" min="25000000"
                  onChange={(e) => setAmount(e.target.value)} data-testid="input-invest-amount"/>
                <div className="text-[11px] text-slate-500 mt-1">Min. Rp 25.000.000 · kelipatan Rp 1.000.000</div>
              </div>
              <div>
                <Label>Periode (Tahun)</Label>
                <Input type="number" min="1" max="10" value={years}
                  onChange={(e) => setYears(e.target.value)} data-testid="input-invest-years"/>
              </div>
              <div className="flex items-end">
                <Button onClick={runSim} disabled={busy} data-testid="btn-simulate" className="w-full bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)]">
                  <TrendingUp className="w-4 h-4 mr-2"/> {busy ? "Menghitung..." : "Hitung Proyeksi"}
                </Button>
              </div>
            </div>
          </Card>

          {sim && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
                  <div className="overline mb-2">Pokok Investasi</div>
                  <div className="font-mono-num text-2xl font-bold">{formatIDR(sim.principal)}</div>
                </Card>
                <Card className="p-5 border border-[var(--hotto-border)] rounded-md stat-card">
                  <div className="overline mb-2">Total Keuntungan {sim.years}thn</div>
                  <div className="font-mono-num text-2xl font-bold text-emerald-600">+{formatIDR(sim.total_profit)}</div>
                  <div className="text-xs text-slate-500 mt-1">Compound 14%/tahun</div>
                </Card>
                <Card className="p-5 border-2 border-[var(--hotto-primary)] rounded-md stat-card">
                  <div className="overline mb-2">Saldo Akhir</div>
                  <div className="font-mono-num text-2xl font-bold text-[var(--hotto-primary)]">{formatIDR(sim.total_return)}</div>
                </Card>
              </div>

              <Card className="p-6 border border-[var(--hotto-border)] rounded-md mb-6">
                <div className="overline mb-3">Grafik Proyeksi</div>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#047857" stopOpacity={0.4}/>
                          <stop offset="95%" stopColor="#047857" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0"/>
                      <XAxis dataKey="Tahun" fontSize={12}/>
                      <YAxis tickFormatter={(v) => `${(v/1000000).toFixed(0)}jt`} fontSize={11}/>
                      <Tooltip formatter={(v) => formatIDR(v)}/>
                      <Legend/>
                      <Area type="monotone" dataKey="Saldo Akhir" stroke="#047857" fill="url(#g1)" strokeWidth={2.5}/>
                      <Line type="monotone" dataKey="Keuntungan Kumulatif" stroke="#EA580C" strokeWidth={2}/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </Card>

              <Card className="border border-[var(--hotto-border)] rounded-md overflow-hidden">
                <div className="p-3 border-b bg-slate-50 overline">Proyeksi Tahun demi Tahun</div>
                <Table>
                  <TableHeader><TableRow className="bg-slate-50">
                    <TableHead>Tahun</TableHead>
                    <TableHead className="text-right">Keuntungan Tahun</TableHead>
                    <TableHead className="text-right">Kumulatif</TableHead>
                    <TableHead className="text-right">Saldo</TableHead>
                    <TableHead className="text-right">Return</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {sim.projections.map((p) => (
                      <TableRow key={p.year} className="table-row-hover">
                        <TableCell className="font-semibold">Tahun ke-{p.year}</TableCell>
                        <TableCell className="text-right font-mono-num text-emerald-700">+{formatIDR(p.yearly_profit)}</TableCell>
                        <TableCell className="text-right font-mono-num text-emerald-800">{formatIDR(p.cumulative_profit)}</TableCell>
                        <TableCell className="text-right font-mono-num font-bold">{formatIDR(p.balance)}</TableCell>
                        <TableCell className="text-right"><Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">+{((p.balance / sim.principal - 1) * 100).toFixed(1)}%</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </TabsContent>

        {/* ================== AGREEMENT ================== */}
        <TabsContent value="agreement">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div><Label>Nama Investor</Label><Input value={investorName} onChange={(e) => setInvestorName(e.target.value)} data-testid="input-investor-name"/></div>
            <div><Label>Nominal (Rp)</Label><Input type="number" value={amount} step="1000000" onChange={(e) => setAmount(e.target.value)}/></div>
            <div className="flex items-end">
              <Button onClick={downloadPDF} className="bg-[var(--hotto-primary)] hover:bg-[var(--hotto-primary-hover)] w-full" data-testid="btn-download-agreement">
                <Download className="w-4 h-4 mr-2"/> Download PDF
              </Button>
            </div>
          </div>

          <Card className="p-8 border border-[var(--hotto-border)] rounded-md bg-white">
            <div ref={agreementRef} className="prose max-w-none">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold mb-1" style={{fontFamily:'Outfit'}}>SURAT PERJANJIAN BAGI HASIL INVESTASI</h1>
                <div className="text-slate-500 italic">Profit Sharing Investment Agreement</div>
              </div>

              <p className="text-sm">
                Perjanjian ini dibuat pada tanggal {new Date().toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" })}, antara:
                <br/><em>This agreement is made on the date above, between:</em>
              </p>

              <div className="my-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="p-3 border border-slate-200 rounded">
                  <b>PIHAK PERTAMA / FIRST PARTY</b><br/>
                  Nama: <b>Hotto Branch Cianjur</b> ("Perusahaan" / "the Company")<br/>
                  Alamat: Cianjur, Jawa Barat, Indonesia<br/>
                  Bertindak atas nama badan usaha Project Hotto.
                </div>
                <div className="p-3 border border-slate-200 rounded">
                  <b>PIHAK KEDUA / SECOND PARTY</b><br/>
                  Nama: <b>{investorName || "________________"}</b> ("Investor")<br/>
                  Nominal Investasi: <b>{formatIDR(amount)}</b><br/>
                  Bertindak untuk diri sendiri.
                </div>
              </div>

              <p className="text-sm">
                Kedua belah pihak sepakat untuk mengadakan perjanjian bagi hasil investasi dengan ketentuan sebagai berikut:
                <br/><em>Both parties agree to enter into a profit-sharing investment agreement with the following terms:</em>
              </p>

              <table className="w-full mt-4 text-sm">
                <tbody>
                  {[
                    {
                      no: "1", title_id: "OBJEK PERJANJIAN", title_en: "SUBJECT OF AGREEMENT",
                      id: "Perusahaan menerima dana investasi dari Investor sebesar nominal yang tercantum di atas untuk digunakan dalam pengembangan usaha Project Hotto.",
                      en: "The Company receives investment funds from the Investor in the amount stated above to be used for developing the Project Hotto business.",
                    },
                    {
                      no: "2", title_id: "BESARAN BAGI HASIL", title_en: "PROFIT SHARING RATE",
                      id: "Perusahaan memberikan imbal hasil sebesar 14% (empat belas persen) per tahun dari nominal pokok investasi kepada Investor, dihitung secara compound (bunga majemuk).",
                      en: "The Company shall provide a return of 14% (fourteen percent) per year on the principal, calculated on a compound basis.",
                    },
                    {
                      no: "3", title_id: "JANGKA WAKTU", title_en: "TERM",
                      id: "Investasi berlaku minimum 1 (satu) tahun sejak tanggal penandatanganan dan dapat diperpanjang secara otomatis atas persetujuan kedua belah pihak.",
                      en: "The investment shall have a minimum term of 1 (one) year from the signing date and may be automatically renewed upon mutual consent.",
                    },
                    {
                      no: "4", title_id: "PEMBAYARAN IMBAL HASIL", title_en: "PAYMENT OF RETURNS",
                      id: "Imbal hasil dibayarkan setahun sekali (annual) pada tanggal ulang tahun perjanjian, atau atas kesepakatan tertulis kedua belah pihak.",
                      en: "Returns shall be paid annually on the anniversary date of this agreement, or as agreed in writing by both parties.",
                    },
                    {
                      no: "5", title_id: "PENCAIRAN POKOK", title_en: "PRINCIPAL WITHDRAWAL",
                      id: "Investor berhak menarik pokok investasi setelah 1 (satu) tahun dengan pemberitahuan tertulis minimum 30 (tiga puluh) hari sebelumnya.",
                      en: "The Investor may withdraw the principal after 1 (one) year with written notice at least 30 (thirty) days in advance.",
                    },
                    {
                      no: "6", title_id: "MINIMUM INVESTASI", title_en: "MINIMUM INVESTMENT",
                      id: "Nominal minimum investasi adalah Rp 25.000.000 (dua puluh lima juta rupiah) dan harus dalam kelipatan Rp 1.000.000 (satu juta rupiah).",
                      en: "The minimum investment is IDR 25,000,000 (twenty-five million rupiah) and must be in multiples of IDR 1,000,000 (one million rupiah).",
                    },
                    {
                      no: "7", title_id: "RISIKO", title_en: "RISK",
                      id: "Kedua belah pihak menyadari bahwa investasi mengandung risiko usaha. Perusahaan berkomitmen menjalankan usaha dengan itikad baik dan tata kelola yang baik.",
                      en: "Both parties acknowledge that investment involves business risks. The Company commits to running the business in good faith and with good governance.",
                    },
                    {
                      no: "8", title_id: "PENYELESAIAN SENGKETA", title_en: "DISPUTE RESOLUTION",
                      id: "Sengketa yang timbul akan diselesaikan secara musyawarah. Jika tidak tercapai, akan diselesaikan melalui jalur hukum di wilayah hukum Kabupaten Cianjur, Jawa Barat.",
                      en: "Disputes shall be resolved through deliberation. If not resolved, they shall be settled through legal proceedings within the jurisdiction of Cianjur Regency, West Java.",
                    },
                    {
                      no: "9", title_id: "KETENTUAN PENUTUP", title_en: "CLOSING PROVISIONS",
                      id: "Perjanjian ini dibuat dalam 2 (dua) rangkap asli, bermeterai cukup, dan mengikat kedua belah pihak sejak ditandatangani.",
                      en: "This agreement is made in 2 (two) original copies, duly stamped, and binding upon both parties from the date of signing.",
                    },
                  ].map((p) => (
                    <tr key={p.no}>
                      <td className="align-top pr-3 py-3 border-t border-slate-100 w-8"><b>Pasal {p.no}</b></td>
                      <td className="align-top py-3 border-t border-slate-100">
                        <div className="font-semibold text-sm">{p.title_id}</div>
                        <div className="text-xs text-slate-500 italic mb-1">{p.title_en}</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                          <div>{p.id}</div>
                          <div className="italic text-slate-600 md:border-l md:pl-3 md:border-slate-200">{p.en}</div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-10 text-sm">
                <div>
                  <div className="mb-1">PIHAK PERTAMA / <em>First Party</em></div>
                  <div className="mb-1">Hotto Branch Cianjur</div>
                  <div className="h-20 border-b border-slate-300 mb-2"></div>
                  <div>(_________________________)</div>
                </div>
                <div>
                  <div className="mb-1">PIHAK KEDUA / <em>Second Party</em></div>
                  <div className="mb-1">{investorName || "________________"}</div>
                  <div className="h-20 border-b border-slate-300 mb-2"></div>
                  <div>(_________________________)</div>
                </div>
              </div>

              <div className="text-center mt-8 text-xs text-slate-400 italic">
                Dokumen ini dihasilkan secara elektronik oleh Sistem Project Hotto · {new Date().toLocaleDateString("id-ID")}
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </>
  );
}
